import { useSelector } from "react-redux";
import { MobileHelper } from "common.ui/Helpers/mobile";
import { ICustomForm, IStoreCustomFormGrid } from "../DataTypes/storeTypes";
import { DisplayHelper } from "../Helpers/displayHelper";
import { CustomFormPrinter } from "common.ui/CustomForms/Printer/customFormPrinter";
import * as Notifications from "../../../Common/Notifications/helper";
import { downloadBlob } from "common.ui/Helpers/downloadBlob";

import pdfMake from "pdfmake/build/pdfmake";
import { useState } from "react";
import { DateUtils } from "../Helpers/dateUtils";
import { ICustomFormEditorParams } from "common.ui/CustomForms/Editor/DataTypes/IStore";

import { Utils } from "common.ui/CustomForms/Utils/misc";
import { Button } from "@powerdiary/components-library/lib";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { ScoreResultDto } from "../../../../UiNew/types/generated/models";

interface IProps {
    customForm: ICustomForm;
}

const statusDisplay = {
    Completed: "Completed",
    Expired: "Expired",
    Incomplete: "Incomplete",
};

export const CustomFormRow = (props: IProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const {
        initialSettings: { downloadUrl, downloadPdfUrl, viewUrl, isGotenbergPrintEnabled },
    } = useSelector((state: IStoreCustomFormGrid) => state);

    const status =
        props.customForm.formStatus === "Locked"
            ? statusDisplay.Completed
            : props.customForm.isExpired
              ? statusDisplay.Expired
              : statusDisplay.Incomplete;

    async function download() {
        setIsLoading(true);
        try {
            if (isGotenbergPrintEnabled) {
                const response = await fetch(
                    `${downloadPdfUrl}?formDataNumber=${props.customForm.customFormDataNumber}`,
                    { method: "GET" }
                );
                if (!response.ok) {
                    Notifications.error("An error occurred while downloading the form");
                    return;
                }
                const blob = await response.blob();
                downloadBlob(blob, `CustomForm_${props.customForm.customFormDataNumber}.pdf`);
                return;
            }

            const params = {
                formDataNumber: props.customForm.customFormDataNumber.toString(),
            };

            const queryString = new URLSearchParams(params).toString();
            const url = `${downloadUrl}?${queryString}`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const data = await response.json();
            if (data.isFailed) {
                Notifications.error(data.errorMessages[0]);
                return;
            }

            const customForm = data.customForm;
            const logoSize = await Utils.getImageSize(data.logoData);
            const customFormPrinter = new CustomFormPrinter(
                {
                    sgCountryCode: customForm.sgCountryCode,
                    shortDateFormat: data.cultureDateFormat,
                } as ICustomFormEditorParams,
                [
                    {
                        ...customForm,
                        isScoreEnabled: Boolean(customForm.isScoreEnabled),
                        scoreVersionNumber: customForm.scoreVersionNumber ?? null,
                        scoreResults: (customForm.scoreResults ?? []).map((s: ScoreResultDto) => ({
                            name: s.name,
                            value: Number(s.value),
                            method: s.method,
                            severityName: s.severityName,
                            severityColor: s.severityColor,
                        })),
                        displayScoresInPortal: Boolean(customForm.displayScoresInPortal),
                    },
                ],
                {
                    organizationName: data.organizationName,
                    clientName: data.clientName,
                    clientNumber: data.clientNumber,
                    clientDateOfBirth: data.clientDateOfBirth,
                    lookups: customForm.lookups,
                    logoUrl: undefined,
                    logoSize: logoSize,
                    logoData: data.logoData,
                    logoPosition: data.logoPosition,
                },
                pdfMake,
                null,
                new DateUtils(data.cultureDateFormat, data.cultureDateTimeFormatJs)
            );

            await customFormPrinter.createBlobPdf((blob: Blob) => {
                downloadBlob(blob, `customForm-${props.customForm.customFormDataNumber}.pdf`);
            });
        } catch (err) {
            Notifications.error("An error occurred while downloading the form");
        } finally {
            setIsLoading(false);
        }
    }

    const viewForm = (): void => {
        const {
            customForm: { customFormDataNumber },
        } = props;

        window.location.href = viewUrl.replace("_number_", `${customFormDataNumber}`);
    };

    return (
        <div className={`custom-form-item ${MobileHelper.isTouchDevice ? "" : "no-touch"}`}>
            <div className="row">
                <div className="col-12 col-md-6">
                    <label className="text-dark-gray form-name">{props.customForm.formName}</label>
                    <div>
                        <span className={`badge badge-secondary badge-${status.toLowerCase()}`}>{status}</span>
                    </div>
                    {props.customForm.isExpired && (
                        <div className="custom-form-date">
                            <label>Expired:</label> {DisplayHelper.toShortDateString(props.customForm.expiryDate)}
                        </div>
                    )}
                    {props.customForm.expiryDate && !props.customForm.isExpired && (
                        <div className="custom-form-date">
                            <label>Expires:</label> {DisplayHelper.toShortDateString(props.customForm.expiryDate)}
                        </div>
                    )}
                    {props.customForm.dateCompleted && (
                        <div className="custom-form-date">
                            <label>Completed:</label> {DisplayHelper.toShortDateString(props.customForm.dateCompleted)}
                        </div>
                    )}
                </div>
                <div className="col-12 col-md-6 text-md-right">
                    {status === statusDisplay.Completed && (
                        <div className="custom-form-actions">
                            <div className="custom-form-actions">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={download}
                                    disabled={isLoading}
                                    startIcon={<FontAwesomeIcon icon={faDownload} />}
                                >
                                    Download
                                </Button>
                            </div>
                        </div>
                    )}
                    {status === statusDisplay.Incomplete && (
                        <div className="custom-form-actions">
                            <Button variant="contained" size="small" onClick={viewForm}>
                                View Form
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
